import { useEffect, useRef, useState } from 'react';
import lottie, { AnimationItem } from 'lottie-web';

/** Плавное появление анимации и исчезновение заставки, мс. */
const FADE_MS = 400;

type WelcomeProps = {
  /**
   * Запасная длительность показа, если анимация не загрузилась. Обычно
   * заставка уходит, как только анимация доиграла.
   */
  holdMs: number;
  /** Заставка исчезла — её можно убирать из дерева. */
  onDone: () => void;
};

/**
 * Приветственная заставка: Lottie-анимация EVOS Rewards (`NewLoader.json`).
 *
 * Лежит слоем поверх приложения: под ней уже рисуется главная, и в конце
 * заставка растворяется в готовый экран. Своего фона у анимации нет (белое
 * «EVOS», жёлтое «Rewards», монеты и полупрозрачные белые блики), поэтому
 * зелёный (#007854) даёт контейнер — он непрозрачен с первого кадра, иначе
 * сквозь него мелькало бы приложение.
 *
 * JSON весит ~0.5 МБ — грузим отдельным чанком, чтобы не тянуть его в
 * основной бандл; зелёный фон виден сразу, анимация проявляется, когда готова.
 */
export const Welcome = ({ holdMs, onDone }: WelcomeProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [animationVisible, setAnimationVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let animation: AnimationItem | null = null;
    let doneTimer = 0;

    const leave = () => {
      if (cancelled || doneTimer) return;
      setLeaving(true);
      doneTimer = window.setTimeout(onDone, FADE_MS);
    };

    // Анимация не загрузилась или зависла — заставка всё равно уходит.
    const fallbackTimer = window.setTimeout(leave, FADE_MS + holdMs + 2000);

    import('../assets/NewLoader.json')
      .then(({ default: animationData }) => {
        if (cancelled || !containerRef.current) return;

        animation = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop: false,
          autoplay: true,
          animationData,
          // Макет 375×812: на любом экране целиком, по центру, без обрезки —
          // поля закрывает зелёный фон контейнера.
          rendererSettings: { preserveAspectRatio: 'xMidYMid meet' },
        });
        animation.addEventListener('complete', leave);
        setAnimationVisible(true);
      })
      .catch(() => {
        window.setTimeout(leave, holdMs);
      });

    return () => {
      cancelled = true;
      window.clearTimeout(fallbackTimer);
      window.clearTimeout(doneTimer);
      animation?.destroy();
    };
    // Анимация и таймеры заводятся один раз на показ заставки.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      aria-hidden
      className="fixed inset-0 z-[9999] bg-[#007854] transition-opacity ease-in-out"
      style={{ opacity: leaving ? 0 : 1, transitionDuration: `${FADE_MS}ms` }}
    >
      <div
        ref={containerRef}
        className="h-full w-full transition-opacity ease-in-out"
        style={{
          opacity: animationVisible ? 1 : 0,
          transitionDuration: `${FADE_MS}ms`,
        }}
      />
    </div>
  );
};
